import { PDFDocument, degrees, rgb } from 'pdf-lib';

/**
 * @name blobToArrayBuffer
 * @desc Convierte un Blob a ArrayBuffer
 * @param {Blob} blob
 * @returns {Promise<ArrayBuffer>}
 */
export const blobToArrayBuffer = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
});

/**
 * @name pdfBytesToBlob
 * @desc Convierte bytes de PDF a Blob
 * @param {Uint8Array} pdfBytes
 * @returns {Blob}
 */
export const pdfBytesToBlob = (pdfBytes) => new Blob([pdfBytes], { type: 'application/pdf' });

/**
 * @name base64ToArrayBuffer
 * @desc Convierte base64 a ArrayBuffer
 * @param {string} base64
 * @returns {ArrayBuffer}
 */
export const base64ToArrayBuffer = (base64) => {
    // Remover prefijo data URL si existe
    const base64Clean = base64.includes(',') ? base64.split(',')[1] : base64;
    const binaryString = window.atob(base64Clean);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

/**
 * @name dataUrlToArrayBuffer
 * @desc Convierte data URL de imagen a ArrayBuffer
 * @param {string} dataUrl
 * @returns {Promise<ArrayBuffer>}
 */
export const dataUrlToArrayBuffer = async (dataUrl) => {
    const response = await fetch(dataUrl);
    return response.arrayBuffer();
};

/**
 * @name dataUrlToPngDataUrl
 * @desc Redibuja una imagen en un canvas para obtenerla como PNG. Se usa con
 * formatos que pdf-lib no sabe embeber (webp, gif, bmp...)
 * @param {string} dataUrl
 * @returns {Promise<string>}
 */
const dataUrlToPngDataUrl = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = dataUrl;
});

/**
 * @name embedSignatureImage
 * @desc Embebe la firma segun su formato real. La pestana "Imagen" acepta JPG,
 * y embedPng lanza error con un JPG, con lo que la firma nunca se estampaba
 * @param {PDFDocument} pdfDoc
 * @param {string} dataUrl
 * @returns {Promise<PDFImage>}
 */
const embedSignatureImage = async (pdfDoc, dataUrl) => {
    const mime = (dataUrl.match(/^data:([^;,]+)/) || [])[1] || '';

    if (mime === 'image/png') {
        return pdfDoc.embedPng(await dataUrlToArrayBuffer(dataUrl));
    }
    if (mime === 'image/jpeg' || mime === 'image/jpg') {
        return pdfDoc.embedJpg(await dataUrlToArrayBuffer(dataUrl));
    }

    const pngDataUrl = await dataUrlToPngDataUrl(dataUrl);
    return pdfDoc.embedPng(await dataUrlToArrayBuffer(pngDataUrl));
};

/**
 * @name toPageSpace
 * @desc Convierte coordenadas de la vista (las que entrega pdf.js: origen abajo
 * a la izquierda de la pagina tal como se ve) a coordenadas reales de la pagina.
 * pdf.js ya aplica el origen del CropBox y la rotacion de la pagina, pdf-lib no;
 * sin esta conversion la firma queda fuera del area visible en esas paginas
 * @param {PDFPage} page
 * @param {number} vx
 * @param {number} vy
 * @returns {{x: number, y: number, rotation: number}}
 */
const toPageSpace = (page, vx, vy) => {
    const {
        x: bx, y: by, width: w, height: h,
    } = page.getCropBox();
    const rotation = ((page.getRotation().angle % 360) + 360) % 360;

    switch (rotation) {
        case 90:
            return { x: bx + w - vy, y: by + vx, rotation };
        case 180:
            return { x: bx + w - vx, y: by + h - vy, rotation };
        case 270:
            return { x: bx + vy, y: by + h - vx, rotation };
        default:
            return { x: bx + vx, y: by + vy, rotation: 0 };
    }
};

// Espacio bajo la firma para el texto "Firmado digitalmente - fecha"
const SIGNATURE_TEXT_MARGIN = 15;

/**
 * @name clampToPage
 * @desc Mantiene la firma y su texto "Firmado digitalmente" dentro de la hoja.
 * La firma se centra en el clic, asi que un clic cerca del borde la dejaba en
 * parte fuera de la pagina
 * @param {Object} position - {x, y, width, height, ...}
 * @param {number} pageW - ancho de la pagina en puntos
 * @param {number} pageH - alto de la pagina en puntos
 * @returns {Object}
 */
export const clampToPage = (position, pageW, pageH) => {
    const limitar = (valor, min, max) => Math.min(Math.max(valor, min), Math.max(max, min));
    return {
        ...position,
        x: limitar(position.x, 0, pageW - position.width),
        y: limitar(position.y, SIGNATURE_TEXT_MARGIN, pageH - position.height),
    };
};

/**
 * @name resolveDropPosition
 * @desc Reubica una firma que se termino de arrastrar en la pagina sobre la que
 * quedo. Al arrastrar, la firma sigue anclada a la pagina donde se hizo clic
 * aunque visualmente se lleve a otra; sin esto se estampaba en la pagina de
 * origen con coordenadas fuera de la hoja y no se veia en el documento.
 * Usa los canvas que dibuja PdfRenderer (atributo data-page-index).
 * @param {Object} position - {x, y, width, height, pageIndex, canvasHeight, scale}
 * @param {number} scale - escala con la que estan dibujadas las paginas
 * @returns {Object} posicion en la pagina donde se solto la firma
 */
export const resolveDropPosition = (position, scale) => {
    const canvases = Array.from(document.querySelectorAll('canvas[data-page-index]'));
    const origin = canvases.find((c) => Number(c.dataset.pageIndex) === position.pageIndex);
    if (!origin) return position;

    // Esquina superior izquierda de la firma en pantalla, a partir de su posicion
    // respecto a la pagina de origen (puede estar fuera de esa pagina)
    const originRect = origin.getBoundingClientRect();
    const originPageH = position.canvasHeight / position.scale;
    const pxPorPunto = originRect.height / originPageH;
    const left = originRect.left + position.x * pxPorPunto;
    const top = originRect.top
        + (originPageH - position.y - position.height) * pxPorPunto;
    const centroY = top + (position.height * pxPorPunto) / 2;

    // Pagina bajo el centro de la firma; si cae en el espacio entre dos
    // paginas, la mas cercana
    let target = origin;
    let menorDistancia = Infinity;
    canvases.forEach((c) => {
        const r = c.getBoundingClientRect();
        let distancia = 0;
        if (centroY < r.top) distancia = r.top - centroY;
        else if (centroY > r.bottom) distancia = centroY - r.bottom;
        if (distancia < menorDistancia) {
            menorDistancia = distancia;
            target = c;
        }
    });

    const targetRect = target.getBoundingClientRect();
    const pageW = target.width / scale;
    const pageH = target.height / scale;
    const puntoPorPx = pageH / targetRect.height;
    const yTop = (top - targetRect.top) * puntoPorPx;

    return clampToPage({
        ...position,
        x: (left - targetRect.left) * puntoPorPx,
        y: pageH - yTop - position.height,
        pageIndex: Number(target.dataset.pageIndex),
        canvasHeight: target.height,
        scale,
    }, pageW, pageH);
};

/**
 * @name addSignatureToPdf
 * @desc Agrega una imagen de firma al PDF en la posicion especificada
 * @param {ArrayBuffer} pdfBytes - Bytes del PDF original
 * @param {string} signatureDataUrl - Data URL de la imagen de firma (PNG o JPG base64)
 * @param {Object} position - {x, y, width, height, pageIndex}
 * @returns {Promise<Uint8Array>} - PDF modificado como bytes
 */
export const addSignatureToPdf = async (
    pdfBytes, signatureDataUrl, positions,
) => {
    const pdfDoc = await PDFDocument.load(pdfBytes);

    // Imagen de la firma (se embebe una vez, se reutiliza)
    const signatureImage = await embedSignatureImage(pdfDoc, signatureDataUrl);

    const pages = pdfDoc.getPages();

    // Normalizar: aceptar posicion unica o array de posiciones
    const positionsArray = Array.isArray(positions)
        ? positions : [positions];

    const timestamp = new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    // Dibujar cada firma en su pagina correspondiente
    positionsArray.forEach((position) => {
        // En firma masiva la posicion se copia de otro documento; si este tiene
        // menos paginas se usa la ultima, que es donde va el bloque de firmas
        const pageIndex = Math.min(position.pageIndex || 0, pages.length - 1);
        const page = pages[pageIndex];
        const signatureWidth = position.width || 150;
        const signatureHeight = position.height || 50;

        const imageAnchor = toPageSpace(page, position.x, position.y);
        page.drawImage(signatureImage, {
            x: imageAnchor.x,
            y: imageAnchor.y,
            width: signatureWidth,
            height: signatureHeight,
            rotate: degrees(imageAnchor.rotation),
        });

        const textAnchor = toPageSpace(page, position.x, position.y - 15);
        page.drawText(`Firmado digitalmente - ${timestamp}`, {
            x: textAnchor.x,
            y: textAnchor.y,
            size: 8,
            color: rgb(0.5, 0.5, 0.5),
            rotate: degrees(textAnchor.rotation),
        });
    });

    return pdfDoc.save();
};

/**
 * @name getPdfPageCount
 * @desc Obtiene el numero de paginas de un PDF
 * @param {ArrayBuffer} pdfBytes
 * @returns {Promise<number>}
 */
export const getPdfPageCount = async (pdfBytes) => {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    return pdfDoc.getPageCount();
};

/**
 * @name downloadPdf
 * @desc Descarga un PDF individual
 * @param {Blob} pdfBlob - Blob del PDF
 * @param {string} filename - Nombre del archivo (sin extension)
 */
export const downloadPdf = (pdfBlob, filename = 'documento_firmado') => {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * @name downloadMultiplePdfsAsZip
 * @desc Descarga multiples PDFs en un archivo ZIP
 * @param {Array} documents - Array de {blob, name}
 * @param {string} zipFilename - Nombre del archivo ZIP
 * @returns {Promise<void>}
 */
export const downloadMultiplePdfsAsZip = async (documents, zipFilename = 'documentos_firmados') => {
    // Importar JSZip dinamicamente
    const JSZip = (await import('jszip')).default;
    const { saveAs } = await import('file-saver');

    const zip = new JSZip();

    // Agregar cada documento al ZIP
    documents.forEach((doc, index) => {
        const name = doc.name || `documento_${index + 1}`;
        // Asegurar que el nombre termine en .pdf
        const fileName = name.endsWith('.pdf') ? name : `${name}.pdf`;
        zip.file(fileName, doc.blob);
    });

    // Generar y descargar el ZIP
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${zipFilename}.zip`);
};
